import type { AgentMode, RunnerMode, TaskContext } from "@project/shared";

const SP_DESC_MAX_CHARS = 80;

function truncateDescription(desc: string, maxChars: number): string {
  if (desc.length <= maxChars) return desc;
  return desc.slice(0, maxChars) + "…";
}

type ProjectTag = NonNullable<TaskContext["projectTags"]>[number];

function formatTagWithContextPaths(tag: ProjectTag): string[] {
  const desc = tag.description ? ` — ${tag.description}` : "";
  const lines = [`- Name: "${tag.name}"${desc}`];
  for (const link of tag.contextPaths ?? []) {
    const label = link.label ? ` (${link.label})` : "";
    lines.push(`    → ${link.type}: ${link.path}${label}`);
  }
  return lines;
}

function buildPropertyInstructions(context: TaskContext, runnerMode?: RunnerMode): string[] {
  const isTask = runnerMode === "task";
  const parts: string[] = [];
  parts.push(
    ``,
    `### Proactive Property Management`,
    `As you plan this task, proactively fill in task properties when you have enough context:`,
    `- Use update_task_properties to set any combination of: title, story points, and tags`,
    `- You can update all properties at once or just one at a time as needed`,
    `- Icons are assigned automatically during identification — do not set icons manually`,
    ``,
    `Don't wait for the user to ask — fill these in naturally as the plan takes shape.`,
    `If the user adjusts the plan significantly, update the properties to match.`,
  );

  // Story points — PM/code-review only
  if (!isTask && context.storyPoints && context.storyPoints.length > 0) {
    parts.push(``, `Available story point tiers:`);
    for (const sp of context.storyPoints) {
      const desc = sp.description
        ? ` — ${truncateDescription(sp.description, SP_DESC_MAX_CHARS)}`
        : "";
      parts.push(`- Value ${sp.value}: "${sp.name}"${desc}`);
    }
  }

  if (context.projectTags && context.projectTags.length > 0) {
    const assignedIds = new Set(context.taskTagIds ?? []);
    const assigned = context.projectTags.filter((t) => assignedIds.has(t.id));
    const unassigned = context.projectTags.filter((t) => !assignedIds.has(t.id));

    if (assigned.length > 0) {
      parts.push(``, `Assigned tags:`);
      for (const tag of assigned) parts.push(...formatTagWithContextPaths(tag));
    }

    // Unassigned tags — PM/code-review only, include context paths so agents can see linked files
    if (!isTask && unassigned.length > 0) {
      parts.push(``, `Available project tags:`);
      for (const tag of unassigned) parts.push(...formatTagWithContextPaths(tag));
    }
  }

  return parts;
}

/**
 * Auto tasks boot straight into building with no plan-approval step — but the
 * plan must land on the card BEFORE code is written, so the team can see the
 * intent while the build runs. This section teaches post-then-build: saving
 * the plan is a required first step, never an approval wait.
 */
function buildPlanDocumentationSection(context?: TaskContext): string[] {
  const hasPlan = !!context?.plan?.trim();
  return [
    ``,
    `### Plan & Properties (post the plan BEFORE building — no approval wait)`,
    `- The card is already In Progress and advances automatically (In Progress → Review PR when you open the PR). There is no plan-approval step.`,
    ...(hasPlan
      ? [
          `- A plan is already saved on the card. Keep it current with update_task_plan if your approach diverges materially from it.`,
        ]
      : [
          `- No plan is saved yet: BEFORE writing any code, investigate briefly (search first, read only critical files) and save a concise implementation plan with update_task_plan (file:line citations). Then start implementing immediately — the plan is a record for the team, not a gate; never pause for approval.`,
        ]),
    `- Identification auto-fills title, story points, and icon with quick AI guesses. After exploring, refine the title and story points with update_task_properties if they look like placeholders. Icons are automatic — never set them.`,
  ];
}

function buildExplorationMethodology(): string[] {
  return [
    ``,
    `### Exploration Methodology`,
    `Investigate efficiently — do not read files aimlessly:`,
    `- Search first, read second: use grep/glob to locate relevant code, then read only the files that matter`,
    `- Never re-read a file already in your context — you have a large context window, scroll up instead`,
    `- Start with 3-5 critical files, form a hypothesis about the approach, then validate with targeted reads`,
    `- Stop exploring when you can cite specific \`file.ts:line\` locations and function names for every step in your plan — that's enough`,
  ];
}

function buildPlanCitationFormat(): string[] {
  return [
    ``,
    `### Plan Citation Format`,
    `Plans must ground each change in the code. For every step that touches code:`,
    `- Cite the exact location as \`path/from/repo/root.ts:lineNumber\` (e.g. \`packages/conveyor-agent/src/execution/mode-prompt.ts:129\`).`,
    `- Name the specific function, class, constant, or JSX element being touched.`,
    `- When behavior hinges on a short piece of code, quote 1–3 lines inline instead of paraphrasing.`,
    `- Ranges are fine for larger edits (\`foo.ts:120-145\`). Do not cite whole files without a line.`,
    `- If a file doesn't exist yet, write \`NEW: path/to/new-file.ts\` and describe the surrounding module it fits into.`,
  ];
}

function buildDiscoveryPrompt(context?: TaskContext, runnerMode?: RunnerMode): string {
  const parts = [
    `\n## Mode: Discovery`,
    `You are in Discovery mode — helping plan and scope this task.`,
    `- You have read-only codebase access (can read files, run git commands, search code)`,
    `- You can write plan files in .claude/plans/ only — no other file writes. Plan files on disk are NOT synced to the task; save the plan via update_task_plan`,
    `- Do NOT attempt to edit, write, or modify source code files — these operations will be denied`,
    `- If you identify code changes needed, describe them in the plan instead of implementing them`,
    `- You can create and manage subtasks`,
    `- Goal: collaborate with the user to create a clear plan`,
    `- Proactively fill task properties (SP, tags) as the plan takes shape`,
    ``,
    `### Planning Checklist (complete ALL before calling ExitPlanMode)`,
    `Your PRIMARY goal is to create a thorough plan. Complete these steps in order:`,
    `1. Read the task description and chat history — respond to what's been discussed`,
    `2. Investigate the codebase using the methodology below — search first, read targeted files`,
    `3. Save a detailed plan via \`update_task_plan\``,
    `4. Set story points, tags, and title via \`update_task_properties\` (icon is set automatically)`,
    `5. Discuss the plan with the team if they're engaged, incorporate feedback`,
    `6. THEN call ExitPlanMode — it is the LAST step, not the first`,
    ...buildExplorationMethodology(),
    ...buildPlanCitationFormat(),
    ``,
    `### Self-Identification Tools`,
    `Use these MCP tools to set your own task properties:`,
    `- \`update_task_plan\` — save your plan and description`,
    `- \`update_task_properties\` — set title, story points, and tags (any combination)`,
    `Note: Icons are assigned automatically during identification after planning is complete.`,
    ``,
    `### Tags & Context`,
    `- Early in discovery, identify relevant project tags that match this task's domain`,
    `- Add matching tags using \`update_task_properties\` — this links relevant documentation and rules that help you plan more effectively`,
    `- Tags accelerate discovery by surfacing domain-specific context automatically`,
    ``,
    ...(context?.isParentTask
      ? [
          `### Parent Task Coordination`,
          `You are a parent task with child tasks. Focus on breaking work into child tasks with detailed plans, not planning implementation for yourself.`,
          `- Use \`list_subtasks\` to review existing children. Create or update child tasks using \`create_subtask\` / \`update_subtask\`.`,
          `- Each child task should be a self-contained unit of work with a clear plan.`,
          `- Set ordering as explicit **card metadata**, not prose: pass \`dependsOn\` (sibling ids/slugs) on \`create_subtask\` for any child that blocks on another. Leave independent children with no dependencies so the pack runner fans them out in parallel. Do NOT encode "do X after Y" only in the plan text — the runner schedules off dependsOn, not narrative.`,
        ]
      : [
          `### Self-Update vs Subtasks`,
          `- If the work fits in a single task (1-3 SP), update YOUR OWN plan and properties — do not create subtasks`,
          `- Only create subtasks when the work genuinely requires multiple independent pieces (e.g., Pack-tier work, 8+ SP)`,
        ]),
    ``,
    `### Subtask Plan Requirements`,
    `When creating subtasks, each MUST include a detailed \`plan\` field:`,
    `- Plans should be multi-step implementation guides, not vague descriptions`,
    `- Include concrete \`file.ts:line\` citations, function/symbol names, and short code snippets where relevant`,
    `- Reference existing implementations when relevant (e.g., "follow the pattern in src/services/foo.ts")`,
    `- Include testing requirements and acceptance criteria`,
    `- Set \`storyPointValue\` based on estimated complexity`,
    `- Express cross-child ordering as \`dependsOn\` (sibling ids/slugs) on \`create_subtask\` — explicit dependency metadata, not "after task 2" phrasing in the plan. Independent children get no dependencies so they run in parallel.`,
    ``,
    `### Plan Verification Requirements`,
    `Every plan MUST include a **Testing / Verification** section so the build agent has a clear definition of "done". Enumerate:`,
    `- The scoped verification for the change: \`bun run check\` (lint + typecheck) plus \`bun run test:affected\` — name the specific package suites the diff will touch. Docs-only plans should state that no local gates are needed (CI validates on the PR).`,
    `- Any task-specific end-to-end checks (manual UI walk-through, API smoke test, migration dry-run, etc.)`,
    `You are NOT expected to run these gates yourself — discovery is read-only. Just describe them in the plan.`,
    ``,
    `### Completing Planning`,
    `Once ALL checklist items above are done, call the **ExitPlanMode** tool.`,
    `- Required before ExitPlanMode will succeed: **plan** (via update_task_plan), **story points** (via update_task_properties), **title** (via update_task_properties)`,
    `- ExitPlanMode validates these properties and marks planning as complete`,
    `- It does NOT start building — the team controls when to switch to Build mode`,
    `- Do NOT call ExitPlanMode until you have thoroughly explored the codebase and saved a detailed plan`,
  ];
  if (context) parts.push(...buildPropertyInstructions(context, runnerMode));
  return parts.join("\n");
}

function buildAutoPrompt(context?: TaskContext, runnerMode?: RunnerMode): string {
  const parts = [
    `\n## Mode: Auto`,
    `You are in Auto mode — operating autonomously through building → PR.`,
    `- You have full coding access (read, write, edit, bash, git) from the start — there is no read-only planning phase`,
    `- Safety rules: no destructive operations, use --force-with-lease instead of --force`,
    ...buildPlanDocumentationSection(context),
    ``,
    `### Subtask Plan Requirements`,
    `When creating subtasks, each MUST include a detailed \`plan\` field:`,
    `- Plans should be multi-step implementation guides, not vague descriptions`,
    `- Include concrete \`file.ts:line\` citations, function/symbol names, and short code snippets where relevant`,
    `- Reference existing implementations when relevant`,
    `- Include testing requirements and acceptance criteria`,
    `- Set \`storyPointValue\` based on estimated complexity`,
    `- Express cross-child ordering as \`dependsOn\` (sibling ids/slugs) on \`create_subtask\` — explicit dependency metadata, not "after task 2" phrasing in the plan. Independent children get no dependencies so they run in parallel.`,
    ``,
    ...buildPlanCitationFormat(),
    ``,
    ...(context?.isParentTask
      ? [
          ``,
          `### Parent Task Guidance`,
          `You are a parent task — coordinate child tasks instead of implementing directly.`,
          `If no children exist yet, break the work down now: save a parent-level plan with update_task_plan, then create child tasks with create_subtask (each with a detailed plan).`,
          `Child task status lifecycle: Open → InProgress → ReviewPR → ReviewDev → Complete.`,
          `Set child ordering with \`dependsOn\` (sibling ids/slugs) on \`create_subtask\` — explicit metadata the pack runner schedules off, not order described in plan text. Independent children get none and run in parallel.`,
        ]
      : []),
    ``,
    `### Autonomous Guidelines:`,
    `- Make decisions independently — do not ask the team for approval at each step`,
    `- Only escalate when genuinely blocked (ambiguous requirements, missing access, conflicting instructions)`,
    `- Investigate efficiently: search (grep/glob) to locate code, read only critical files, form a hypothesis, validate, then plan`,
  ];
  if (context) parts.push(...buildPropertyInstructions(context, runnerMode));
  return parts.join("\n");
}

export function buildModePrompt(
  agentMode: AgentMode | null | undefined,
  context?: TaskContext,
  runnerMode?: RunnerMode,
): string | null {
  switch (agentMode) {
    case "discovery":
      return buildDiscoveryPrompt(context, runnerMode);
    case "building": {
      const parts = [
        `\n## Mode: Building`,
        `You are in Building mode — executing the plan.`,
        `- You have full coding access (read, write, edit, bash, git)`,
        `- Safety rules: no destructive operations, use --force-with-lease instead of --force`,
        ...(context?.isParentTask
          ? [
              `- You are a parent task. Use \`list_subtasks\`, \`start_child_cloud_build\`, and subtask management tools to coordinate children.`,
              `- Do NOT implement code directly — fire child builds and review their work.`,
              `- Goal: coordinate child task execution and ensure all children complete successfully`,
            ]
          : [
              `- If this is a leaf task (no children): execute the plan directly`,
              `- Goal: implement the plan, run scoped verification, open a PR when done`,
              ``,
              `### Pre-PR Verification Checklist`,
              `CI runs the FULL suite (lint, typecheck, all test shards) on every PR — do not duplicate it locally. Before calling \`mcp__conveyor__create_pull_request\`, scope verification to your diff:`,
              `1. \`bun run check\` — lint + typecheck (fast; run whenever you changed code)`,
              `2. \`bun run test:affected\` — runs only the tests your diff can affect (docs-only diffs run nothing; apps/api diffs run unit tests only since CI covers the int shards; shared/db diffs escalate to the full suite automatically)`,
              `Docs/markdown/.claude-only changes need NO local gates — open the PR and let CI validate.`,
              `If a gate fails, fix it before opening the PR. Do NOT open PRs with known failing gates. Never run the full \`bun run test\` for a diff confined to one package.`,
              `For refactors: also run \`git diff ${context?.baseBranch ?? "dev"}..HEAD\` and confirm the public API surface (exports, function signatures) has no unintended breaking changes.`,
              ...(context?.isAuto || !context?.plan?.trim()
                ? buildPlanDocumentationSection(context)
                : []),
            ]),
      ];
      return parts.join("\n");
    }
    case "review":
      return buildReviewPrompt(context);
    case "auto":
      return buildAutoPrompt(context, runnerMode);
    case "chat":
      return buildChatPrompt();
    default:
      return null;
  }
}

/**
 * Chat mode: a conversational assistant on its own card. It has full file-write
 * access so it can produce files for the user, but it NEVER opens a PR or pushes
 * a branch — deliverables are attached to the card. When the user is done, the
 * agent moves the card InProgress → Complete directly.
 */
function buildChatPrompt(): string {
  return [
    `\n## Mode: Chat`,
    `You are in Chat mode — a conversational assistant working directly with the user on this card.`,
    `- Respond conversationally to the user in chat. Ask clarifying questions when useful; this is a back-and-forth, not an autonomous build.`,
    `- You have full read/write access to the workspace and can run non-destructive shell commands, so you CAN create files (notes, scripts, docs, data, diagrams, etc.) when they help the user.`,
    ``,
    `### Deliverables — attach, do NOT open a PR`,
    `- This card has NO pull-request workflow. Do NOT run \`git push\`, do NOT open a PR, and do NOT rely on branch commits to deliver work — those operations are blocked.`,
    `- When you create a file the user should keep, attach it to the card with the \`upload_attachment\` tool so it shows up on the card. Mention in chat what you attached.`,
    ``,
    `### Finishing the conversation`,
    `- When the user indicates they are done (or explicitly asks to wrap up / close the card), call \`force_update_task_status\` with status \`"Complete"\` to move the card InProgress → Done. There is no review or PR step.`,
    `- If the user is still engaged, keep the card InProgress and keep helping — only complete it once the interaction has concluded.`,
    `- Do not complete the card while you still owe the user a response or an attachment.`,
  ].join("\n");
}

// oxlint-disable-next-line max-lines-per-function -- prompt construction is a single cohesive block
function buildReviewPrompt(context?: TaskContext): string {
  const parts = [
    `\n## Mode: Review`,
    `You are in Review mode — performing code review with fix capability.`,
    `- You have full write access — you can audit code, make fixes, push changes, and run tests`,
    `- Safety rules: no destructive operations, use --force-with-lease instead of --force`,
    ``,
  ];

  if (context?.isParentTask) {
    parts.push(
      `### Parent Task Review`,
      `You are reviewing and coordinating child tasks.`,
      `- Use \`list_subtasks\` to see current child task state and progress.`,
      `- For children in ReviewPR status: review their code quality and merge with \`approve_and_merge_pr\`.`,
      `- For children with failing CI: check with \`get_execution_logs(childTaskId)\` and escalate if stuck.`,
      `- Fire next child builds with \`start_child_cloud_build\` when ready.`,
      `- Create follow-up tasks for issues discovered during review.`,
      ``,
      `### Coordination Workflow`,
      `1. Check child task statuses with \`list_subtasks\``,
      `2. Review completed children — check PRs, run tests if needed`,
      `3. Approve and merge passing PRs`,
      `4. Fire builds for children that are ready`,
      `5. Create follow-up tasks for anything out of scope`,
    );
  } else {
    parts.push(
      `### Code Review Process`,
      `1. Run \`git diff ${context?.baseBranch ?? "dev"}..HEAD\` to see all changes in this PR`,
      `2. Read the task plan to understand the intended changes`,
      `3. Explore the surrounding codebase to verify pattern consistency`,
      `4. Review against the criteria below`,
      ``,
      `### Review Criteria`,
      `- **Correctness**: Does the code do what the plan says? Logic errors, off-by-one, race conditions?`,
      `- **Pattern Consistency**: Does the code follow existing patterns in the codebase? Check nearby files.`,
      `- **Security**: No hardcoded secrets, no injection vulnerabilities, proper input validation at boundaries.`,
      `- **Performance**: No unnecessary loops, no N+1 queries, no blocking in async contexts.`,
      `- **Error Handling**: Appropriate error handling at system boundaries. No swallowed errors.`,
      `- **Test Coverage**: Are new code paths tested? Edge cases covered?`,
      `- **TypeScript Best Practices**: Proper typing (no unnecessary \`any\`), correct React patterns, proper async/await.`,
      `- **Naming & Readability**: Clear names, no misleading comments, self-documenting code.`,
      ``,
      `### Fix Capability`,
      `You have full write access. If you find issues:`,
      `- **Small fixes**: Make the fix directly, commit, and push. Then re-review.`,
      `- **Larger issues**: Use \`request_code_changes\` to flag them for the team.`,
      `- After pushing fixes, wait for CI to pass before approving.`,
      ``,
      `### Output — You MUST do exactly ONE of:`,
      ``,
      `#### If code passes review (or after you've fixed all issues):`,
      `Use the \`approve_code_review\` tool with a brief summary of what looks good.`,
      ``,
      `#### If changes are needed that you cannot fix:`,
      `Use the \`request_code_changes\` tool with specific issues:`,
      `- Reference specific files and line numbers`,
      `- Explain what's wrong and suggest fixes`,
      `- Focus on substantive issues, not style nitpicks (linting handles that)`,
      ``,
      `### Previous Review Feedback`,
      `If previous review feedback is present in the chat history, verify those specific issues were addressed before raising new concerns.`,
      ``,
      `### Rules`,
      `- Do NOT re-review things CI already validates (formatting, lint rules).`,
      `- Be concise — actionable specifics over general observations.`,
      `- Max 5-7 issues per review. Prioritize the most important ones.`,
    );
  }

  return parts.join("\n");
}
